/* global CarouselSlider */

if (!customElements.get('product-card-image-slider')) {
  customElements.whenDefined('carousel-slider').then(() => {
    class ProductCardImageSlider extends CarouselSlider {
      constructor() {
        super();
        this.productCard = this.closest('product-card');
        this.productCardSwatches = this.productCard.querySelectorAll('.card__swatches .opt-btn');
        this.slides = this.querySelectorAll('.slider__item');
        this.paginationButtons = this.querySelectorAll('.product-card-image-slider__dot');

        this.productCard.addEventListener('change', this.handleSwatchChange.bind(this));
      }

      init() {
        super.init();

        this.slider.addEventListener('scroll', this.scrollInProgress.bind(this));
        this.slider.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
        this.slider.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
        this.slider.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
        this.slider.addEventListener('click', this.handleSliderClick.bind(this), true);

        // If swatches are enabled, mark the swatch anchors in the slideshow
        if (this.productCardSwatches) {
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          const activeSwatchId = this.productCard.querySelector('.card__swatches .opt-btn:checked')?.dataset.mediaId || activeSlide?.dataset.mediaId;
          let activeSwatchSlide;

          if (activeSwatchId) {
            this.productCardSwatches.forEach((swatch) => {
              const swatchSlide = this.querySelector(`[data-media-id="${swatch.dataset.mediaId}"]`);
              if (swatchSlide) {
                swatchSlide.setAttribute('data-swatch-anchor', 'true');

                // Set the active set of slideshow images
                if (swatchSlide.dataset.mediaId === activeSwatchId) {
                  activeSlide?.removeAttribute('aria-current');
                  swatchSlide.setAttribute('aria-current', 'true');
                  activeSwatchSlide = swatchSlide;
                }
              }
            });
          }

          if (activeSwatchSlide) this.handleSwatchChange(null, activeSwatchSlide.dataset.mediaId);
        } else {
          // Show the current slider item
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          if (activeSlide) {
            this.handleSwatchChange(null, activeSlide.dataset.mediaId);
          } else {
            this.slider.slides[1]?.setAttribute('aria-current', 'true');
          }
        }

        this.updatePagination(this.querySelector('.slider__item[aria-current]')?.dataset.mediaId);
      }

      /**
       * Starts explicit touch tracking so image links cannot block a horizontal swipe.
       * @param {TouchEvent} evt - Touch start event.
       */
      handleTouchStart(evt) {
        if (evt.touches.length !== 1) return;

        this.touchStartX = evt.touches[0].clientX;
        this.touchStartY = evt.touches[0].clientY;
        this.touchStartScrollLeft = this.slider.scrollLeft;
        this.touchDeltaX = 0;
        this.isHorizontalSwipe = false;
        this.didSwipe = false;
      }

      /**
       * Moves the slider only when the gesture is primarily horizontal.
       * @param {TouchEvent} evt - Touch move event.
       */
      handleTouchMove(evt) {
        if (this.touchStartX == null || evt.touches.length !== 1) return;

        const deltaX = evt.touches[0].clientX - this.touchStartX;
        const deltaY = evt.touches[0].clientY - this.touchStartY;

        if (!this.isHorizontalSwipe) {
          if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
          if (Math.abs(deltaY) >= Math.abs(deltaX)) {
            this.touchStartX = null;
            return;
          }
          this.isHorizontalSwipe = true;
        }

        evt.preventDefault();
        this.didSwipe = true;
        this.touchDeltaX = deltaX;
        this.slider.scrollLeft = this.touchStartScrollLeft - deltaX;
      }

      /**
       * Snaps the gallery to the closest visible image after a swipe.
       */
      handleTouchEnd() {
        if (!this.didSwipe) {
          this.touchStartX = null;
          return;
        }

        const visibleSlides = Array.from(this.slides).filter((slide) => !slide.hidden);
        const startSlide = visibleSlides.reduce((closest, slide) => (
          Math.abs(slide.offsetLeft - this.touchStartScrollLeft)
            < Math.abs(closest.offsetLeft - this.touchStartScrollLeft) ? slide : closest
        ), visibleSlides[0]);
        const startIndex = visibleSlides.indexOf(startSlide);
        let targetIndex = startIndex;

        if (Math.abs(this.touchDeltaX) >= 16) {
          targetIndex += this.touchDeltaX < 0 ? 1 : -1;
        }
        targetIndex = Math.max(0, Math.min(targetIndex, visibleSlides.length - 1));
        const targetSlide = visibleSlides[targetIndex];

        if (targetSlide) {
          this.slider.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
          this.updatePagination(targetSlide.dataset.mediaId);
        }
        this.touchStartX = null;
      }

      /**
       * Prevents a product link from opening immediately after a swipe.
       * @param {MouseEvent} evt - Click generated after the touch gesture.
       */
      handleSliderClick(evt) {
        if (!this.didSwipe) return;

        evt.preventDefault();
        evt.stopPropagation();
        this.didSwipe = false;
      }

      /**
       * Updates the active mobile pagination dot.
       * @param {string} mediaId - The active media ID.
       */
      updatePagination(mediaId) {
        this.paginationButtons.forEach((button) => {
          if (button.dataset.mediaId === mediaId) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
      }

      /**
       * Updates the visibility of slides based on the state of card swatches.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      updateSlideVisibility(mediaId) {
        let hideSlide = true;
        let foundMediaId = false;
        this.slides.forEach((slide) => {
          if (!foundMediaId && slide.getAttribute('data-media-id') === mediaId) {
            hideSlide = false;
            foundMediaId = true;
          } else if (foundMediaId && slide.hasAttribute('data-swatch-anchor')) {
            hideSlide = true;
          }

          slide.toggleAttribute('hidden', hideSlide);

          const paginationButton = Array.from(this.paginationButtons).find(
            (button) => button.dataset.mediaId === slide.dataset.mediaId
          );
          if (paginationButton) paginationButton.hidden = hideSlide;
        });

        this.setButtonStates();
        this.updatePagination(mediaId);
      }

      /**
       * Handles 'change' events in the product card swatches.
       * @param {object} evt - Event object.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      handleSwatchChange(evt, mediaId) {
        const swatchMediaId = evt?.target.dataset.mediaId || mediaId;
        if (swatchMediaId) {
          this.updateSlideVisibility(swatchMediaId);

          const variantMedia = this.querySelector(`[data-media-id="${swatchMediaId}"]`);
          if (variantMedia) {
            const left = variantMedia.closest('.slider__item').offsetLeft;
            this.slider.scrollTo({ left, behavior: 'instant' });
          }
        }
      }

      /**
       * Sets the disabled state of the nav buttons.
       */
      setButtonStates() {
        if (!this.prevBtn && !this.nextBtn) {
          return;
        }

        const currentSlideIndex = Math.round(this.slider.scrollLeft / this.slideSpan) + 1;
        const visibleSlideCount = Array.from(this.slides).filter((slide) => slide.hidden !== true).length;

        this.prevBtn.disabled = currentSlideIndex === 1
          || (this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0);
        this.nextBtn.disabled = visibleSlideCount === currentSlideIndex
          || this.getSlideVisibility(this.slides[this.slides.length - 1]);
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      scrollInProgress() {
        this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      handleScroll() {
        super.handleScroll();
        const currentSlide = this.slides[this.currentIndex];
        if (currentSlide) {
          currentSlide.setAttribute('aria-current', 'true');
          this.updatePagination(currentSlide.dataset.mediaId);
        }
      }
    }

    customElements.define('product-card-image-slider', ProductCardImageSlider);
  });
}
